import { IconArrowLeft, IconArrowRight, IconChartPie } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '@/components/ui/table';

interface NetworkMatchRow {
  level: string;
  sumOfSpend: number;
  percentage: number;
  amount: number;
}
interface NetworkMatchData {
  clientRebatePercentage: number;
  estimatedAnnualCardRebate: number;
  rows: NetworkMatchRow[];
}
interface ProviderEffort {
  name: string;
  percentage: number;
  color: string;
}
interface PaymentMixData {
  type: 'current' | 'cardNetwork';
  check: number;
  electronic: number;
  virtualCard?: number;
}
interface HighNetworkMatch {
  name: string;
  vendorId: string;
  taxId: string;
  type: string;
  transactionCount: number;
  totalSpend: number;
}

interface OtherPeoplesApproachComponentProps {
  companyName: string;
  onNext?: () => void;
  onPrevious?: () => void;
}

export const OtherPeoplesApproachComponent = ({
  companyName,
  onNext,
  onPrevious,
}: OtherPeoplesApproachComponentProps) => {
  const [effectiveRebate, setEffectiveRebate] = useState(0);
  const [hoveredPaymentSegment, setHoveredPaymentSegment] = useState<string | null>(null);
  const [animateMix, setAnimateMix] = useState(false);

  const networkMatchData: NetworkMatchData = {
    clientRebatePercentage: 1.4,
    estimatedAnnualCardRebate: 1248823.39,
    rows: [
      { level: 'High', sumOfSpend: 29238648.02, percentage: 80, amount: 23390918.42 },
      { level: 'Medium', sumOfSpend: 90476430.48, percentage: 50, amount: 45238215.24 },
      { level: 'Low', sumOfSpend: 102862685.85, percentage: 20, amount: 20572537.17 },
      { level: 'No Match', sumOfSpend: 102569424.22, percentage: 0, amount: 0.0 },
    ],
  };
  const providerEffortData: ProviderEffort[] = [
    { name: companyName, percentage: 75.0, color: '#4F46E5' },
    { name: 'Other Providers', percentage: 25.0, color: '#F59E0B' },
  ];
  const paymentMixData: PaymentMixData[] = [
    { type: 'current', check: 65343307.0, electronic: 39132435.0, virtualCard: 1243013.0 },
    { type: 'cardNetwork', check: 66602815.65, electronic: 39115939.35 },
  ];
  const highNetworkMatches: HighNetworkMatch[] = [
    {
      name: 'US Foods',
      vendorId: 'USF-001',
      taxId: '94-1234567',
      type: 'Strategic',
      transactionCount: 182,
      totalSpend: 2519847.23,
    },
    {
      name: 'Booking.com',
      vendorId: 'BKG-204',
      taxId: '45-9876543',
      type: 'Network Name Match',
      transactionCount: 76,
      totalSpend: 1716790.12,
    },
    {
      name: 'Health Plan of Nevada',
      vendorId: 'HPN-320',
      taxId: '11-2039485',
      type: 'Network Name Match',
      transactionCount: 98,
      totalSpend: 2002834.45,
    },
    {
      name: 'Silver State Services',
      vendorId: 'SSS-118',
      taxId: '32-4920348',
      type: 'Network Name Match',
      transactionCount: 141,
      totalSpend: 1790524.11,
    },
    {
      name: 'Express',
      vendorId: 'EXP-773',
      taxId: '22-4039485',
      type: 'Strategic',
      transactionCount: 75,
      totalSpend: 1942389.88,
    },
    {
      name: 'Commodore Lodging',
      vendorId: 'CMD-609',
      taxId: '56-2309485',
      type: 'Strategic',
      transactionCount: 105,
      totalSpend: 1792358.55,
    },
    {
      name: 'Nevada Gaming Commission',
      vendorId: 'NGC-442',
      taxId: '09-3495023',
      type: 'Strategic',
      transactionCount: 64,
      totalSpend: 1684134.03,
    },
    {
      name: 'Starshine',
      vendorId: 'STR-882',
      taxId: '07-0863',
      type: 'Strategic',
      transactionCount: 184,
      totalSpend: 2519847.0,
    },
    {
      name: 'Quick Enterprises',
      vendorId: 'QCK-900',
      taxId: '07-0867',
      type: 'Strategic',
      transactionCount: 178,
      totalSpend: 2573988.0,
    },
    {
      name: 'Porter America',
      vendorId: 'PRT-334',
      taxId: '07-0874',
      type: 'Strategic',
      transactionCount: 82,
      totalSpend: 2219819.0,
    },
    {
      name: 'US Food (West)',
      vendorId: 'USF-101',
      taxId: '07-4862',
      type: 'Strategic',
      transactionCount: 241,
      totalSpend: 1949383.0,
    },
  ];

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  useEffect(() => {
    const targetRebate = 28;
    const duration = 500;
    const steps = 60;
    const increment = targetRebate / steps;
    const stepDuration = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const currentValue = Math.min(increment * currentStep, targetRebate);
      setEffectiveRebate(Math.round(currentValue * 10) / 10);
      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimateMix(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <IconChartPie className="h-6 w-6" />
        <div>
          <h1 className="text-xl font-semibold">Other People's Approach</h1>
          <p className="text-sm text-muted-foreground">
            Competitive analysis and industry benchmarks for {companyName}
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full" />
                Card Network Match Confidence
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 text-xs text-center mb-2 pb-1 border-b">
              <div className="text-muted-foreground font-medium">Sum of Spend</div>
              <div className="text-muted-foreground font-medium">Percentage</div>
              <div className="text-muted-foreground font-medium">Amount</div>
            </div>
            <div className="space-y-2 mb-4">
              {networkMatchData.rows.map((row) => (
                <div key={row.level} className="grid grid-cols-3 gap-2 text-xs py-1">
                  <div className="text-left">
                    <span className="font-medium">{row.level}</span>
                    <div className="text-muted-foreground">
                      ${row.sumOfSpend.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="text-center font-medium">{row.percentage}%</div>
                  <div className="text-right font-medium">
                    ${row.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t text-xs">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">
                  Typical Client Rebate Percentage: {networkMatchData.clientRebatePercentage}%
                </span>
                <span className="text-muted-foreground">
                  Estimated Annual Card Rebate: $
                  {networkMatchData.estimatedAnnualCardRebate.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg text-center">
              Effective Rebate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-8xl font-bold text-blue-600 mb-2 mt-4">{effectiveRebate}%</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg text-center">
              Other Providers Effort
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="relative mx-auto w-32 h-32">
                <svg
                  className="w-full h-full transform -rotate-90"
                  viewBox="0 0 120 120"
                  aria-label="Provider effort distribution chart"
                >
                  <title>Provider effort distribution chart</title>
                  <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    stroke="#F59E0B"
                    strokeWidth="12"
                    strokeDasharray={`${(providerEffortData[1].percentage / 100) * 339.292}, 339.292`}
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    stroke="#4F46E5"
                    strokeWidth="12"
                    strokeDasharray={`${(providerEffortData[0].percentage / 100) * 339.292}, 339.292`}
                    strokeDashoffset={`-${(providerEffortData[1].percentage / 100) * 339.292}`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-foreground">75%</span>
                </div>
              </div>
              <div className="space-y-2 mt-8">
                {providerEffortData.map((provider) => (
                  <div
                    key={provider.name}
                    className="flex items-center justify-center gap-2 text-sm"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: provider.color }}
                    />
                    <span>{provider.name}</span>
                    <span className="font-semibold">{provider.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <h2 className="text-base font-semibold">Payment Mix Comparison</h2>
          </div>
          <div className="space-y-3">
            {paymentMixData.map((data) => {
              const total = data.check + data.electronic + (data.virtualCard || 0);
              const checkWidth = (data.check / total) * 100;
              const electronicWidth = (data.electronic / total) * 100;
              const virtualCardWidth = data.virtualCard ? (data.virtualCard / total) * 100 : 0;
              const checkCenter = checkWidth / 2;
              const electronicCenter = checkWidth + electronicWidth / 2;
              const virtualCardCenter = checkWidth + electronicWidth + virtualCardWidth / 2;
              return (
                <div key={data.type} className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium capitalize">{data.type}</span>
                  </div>
                  <div className="flex h-8 overflow-hidden">
                    <div
                      className="bg-cyan-400 cursor-pointer transition-all duration-700 hover:opacity-80"
                      style={{ width: animateMix ? `${checkWidth}%` : '0%' }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Check payments: ${formatCurrency(data.check)}`}
                      onMouseEnter={() => setHoveredPaymentSegment(`${data.type}-check`)}
                      onMouseLeave={() => setHoveredPaymentSegment(null)}
                    />
                    <div
                      className="bg-indigo-900 cursor-pointer transition-all duration-700 hover:opacity-80"
                      style={{ width: animateMix ? `${electronicWidth}%` : '0%' }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Electronic payments: ${formatCurrency(data.electronic)}`}
                      onMouseEnter={() => setHoveredPaymentSegment(`${data.type}-electronic`)}
                      onMouseLeave={() => setHoveredPaymentSegment(null)}
                    />
                    {data.virtualCard && (
                      <div
                        className="bg-green-500 cursor-pointer transition-all duration-700 hover:opacity-80"
                        style={{ width: animateMix ? `${virtualCardWidth}%` : '0%' }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Virtual card payments: ${formatCurrency(data.virtualCard)}`}
                        onMouseEnter={() => setHoveredPaymentSegment(`${data.type}-virtualcard`)}
                        onMouseLeave={() => setHoveredPaymentSegment(null)}
                      />
                    )}
                  </div>
                  {hoveredPaymentSegment === `${data.type}-check` && (
                    <div
                      className="absolute -top-16 bg-gray-900 text-white px-3 py-2 rounded-sm text-sm font-medium z-30 whitespace-nowrap shadow-lg transform -translate-x-1/2"
                      style={{ left: `${checkCenter}%` }}
                    >
                      <div className="text-center">
                        <div className="font-semibold">Check Payments</div>
                        <div>{formatCurrency(data.check)}</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                    </div>
                  )}
                  {hoveredPaymentSegment === `${data.type}-electronic` && (
                    <div
                      className="absolute -top-16 bg-gray-900 text-white px-3 py-2 rounded-sm text-sm font-medium z-30 whitespace-nowrap shadow-lg transform -translate-x-1/2"
                      style={{ left: `${electronicCenter}%` }}
                    >
                      <div className="text-center">
                        <div className="font-semibold">Electronic Payments</div>
                        <div>{formatCurrency(data.electronic)}</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                    </div>
                  )}
                  {hoveredPaymentSegment === `${data.type}-virtualcard` && data.virtualCard && (
                    <div
                      className="absolute -top-16 bg-gray-900 text-white px-3 py-2 rounded-sm text-sm font-medium z-30 whitespace-nowrap shadow-lg transform -translate-x-1/2"
                      style={{ left: `${virtualCardCenter}%` }}
                    >
                      <div className="text-center">
                        <div className="font-semibold">Virtual Card</div>
                        <div>{formatCurrency(data.virtualCard)}</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-cyan-400 rounded" />
              <span className="text-xs">Check</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-indigo-900 rounded" />
              <span className="text-xs">Electronic</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded" />
              <span className="text-xs">Virtual Card</span>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg">
              Top 15 High Network Matches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table caption="Top 15 high network matches">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Vendor ID</TableHead>
                    <TableHead>Tax ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Transaction Count</TableHead>
                    <TableHead className="text-right">Total Spend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {highNetworkMatches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-xs text-muted-foreground">
                        No high network matches found
                      </TableCell>
                    </TableRow>
                  ) : (
                    highNetworkMatches.map((match) => (
                      <TableRow key={`${match.name}-${match.vendorId}`}>
                        <TableRowHeader className="font-medium">{match.name}</TableRowHeader>
                        <TableCell>{match.vendorId}</TableCell>
                        <TableCell>{match.taxId}</TableCell>
                        <TableCell>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-foreground">
                            {match.type}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {match.transactionCount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${match.totalSpend.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  <TableRow className="border-t-2 font-semibold bg-muted/50">
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right">
                      {highNetworkMatches
                        .reduce((s, r) => s + r.transactionCount, 0)
                        .toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${highNetworkMatches.reduce((s, r) => s + r.totalSpend, 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {(onPrevious || onNext) && (
        <Card>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                {onPrevious && (
                  <Button variant="outline" onClick={onPrevious} className="gap-2">
                    <IconArrowLeft className="h-4 w-4" />
                    Previous: Current State
                  </Button>
                )}
              </div>
              {onNext && (
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <h2 className="font-semibold leading-tight">Ready for next step?</h2>
                    <p className="text-sm text-muted-foreground">
                      Proceed to our approach and solution design
                    </p>
                  </div>
                  <Button onClick={onNext} className="gap-2">
                    Next: Our Approach
                    <IconArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
